import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  PiggyBank, 
  Target,
  Calendar,
  ArrowRight,
  Coins,
  CheckCircle,
  TrendingUp,
  Wallet,
  Calculator,
  Clock
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IndividualSavings = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [savings, setSavings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [selectedSavings, setSelectedSavings] = useState(null);
  const [creating, setCreating] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    contribution_amount: '',
    frequency: 'weekly',
    duration_periods: ''
  });

  useEffect(() => {
    fetchSavings();
  }, []);

  const fetchSavings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/savings`, {
        headers: getAuthHeaders()
      });
      setSavings(response.data);
    } catch (error) {
      toast.error('Failed to load savings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.target_amount || !formData.contribution_amount || !formData.duration_periods) {
      toast.error('Please fill all fields');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/savings`, {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        contribution_amount: parseFloat(formData.contribution_amount),
        frequency: formData.frequency,
        duration_periods: parseInt(formData.duration_periods)
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Savings goal created!');
      setCreateOpen(false);
      setFormData({ name: '', target_amount: '', contribution_amount: '', frequency: 'weekly', duration_periods: '' });
      fetchSavings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create savings');
    } finally {
      setCreating(false);
    }
  };

  const handleDeposit = async () => {
    if (!selectedSavings) return;
    
    setDepositing(true);
    try {
      await axios.post(`${API_URL}/api/savings/deposit`, {
        savings_id: selectedSavings.id
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Deposit successful!');
      setDepositOpen(false);
      setSelectedSavings(null);
      fetchSavings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make deposit');
    } finally {
      setDepositing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedSavings) return;
    
    setWithdrawing(true);
    try {
      await axios.post(`${API_URL}/api/savings/${selectedSavings.id}/withdraw`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Withdrawal successful!');
      setWithdrawOpen(false);
      setSelectedSavings(null);
      fetchSavings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('NGN', '₦');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'completed':
        return <Badge className="bg-[#2272C5]/10 text-[#2272C5]">Completed</Badge>;
      case 'withdrawn':
        return <Badge className="bg-slate-100 text-slate-600">Withdrawn</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const activeSavings = savings.filter(s => s.status === 'active');
  const completedSavings = savings.filter(s => s.status !== 'active');
  const totalSaved = savings.reduce((sum, s) => sum + s.current_amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-32 w-full rounded-2xl mb-4" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="individual-savings-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0B1F3A]">My Savings</h1>
            <p className="text-sm text-slate-600 font-medium">Personal savings goals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/calculator')}
              variant="outline"
              className="rounded-full border-2 border-[#2272C5] text-[#2272C5] font-bold px-3"
              data-testid="calculator-btn"
            >
              <Calculator size={16} className="mr-1.5" />
              <span className="text-xs">Goal Planner</span>
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg" data-testid="create-savings-btn">
                  <Plus size={18} className="mr-1" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-[#0B1F3A] text-xl font-bold">Create Savings Goal</DialogTitle>
                  <DialogDescription className="text-slate-600 font-medium">
                    Set a personal savings target and save daily, weekly, or monthly
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[#0B1F3A] font-bold">Goal Name</Label>
                    <Input
                      placeholder="e.g., Emergency Fund, Vacation"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 rounded-xl border-2"
                      data-testid="savings-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0B1F3A] font-bold">Target Amount (₦)</Label>
                    <Input
                      type="number"
                      placeholder="100000"
                      value={formData.target_amount}
                      onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                      className="h-12 rounded-xl border-2"
                      data-testid="savings-target-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0B1F3A] font-bold">Savings Frequency</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0B1F3A] font-bold">
                      {formData.frequency === 'daily' ? 'Daily' : formData.frequency === 'monthly' ? 'Monthly' : 'Weekly'} Savings Amount (₦)
                    </Label>
                    <Input
                      type="number"
                      placeholder="5000"
                      value={formData.contribution_amount}
                      onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
                      className="h-12 rounded-xl border-2"
                      data-testid="savings-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0B1F3A] font-bold">
                      Duration ({formData.frequency === 'daily' ? 'Days' : formData.frequency === 'monthly' ? 'Months' : 'Weeks'})
                    </Label>
                    <Input
                      type="number"
                      placeholder="20"
                      value={formData.duration_periods}
                      onChange={(e) => setFormData({ ...formData, duration_periods: e.target.value })}
                      className="h-12 rounded-xl border-2"
                      data-testid="savings-duration-input"
                    />
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setCreateOpen(false)} className="font-bold border-2">Cancel</Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={creating}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    data-testid="create-savings-submit"
                  >
                    {creating ? 'Creating...' : 'Create Goal'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="mb-6 bg-gradient-to-r from-[#0B1F3A] to-[#1a3a8f] text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-white/20">
                <PiggyBank size={20} />
              </div>
              <span className="text-white font-medium">Total Saved</span>
            </div>
            <p className="text-3xl font-bold">{formatCurrency(totalSaved)}</p>
            <p className="text-white text-sm mt-1">{activeSavings.length} active goals</p>
          </CardContent>
        </Card>

        {/* Active Savings */}
        {activeSavings.length > 0 ? (
          <div className="space-y-4 mb-6">
            <h2 className="font-semibold text-[#0B1F3A]">Active Goals</h2>
            {activeSavings.map((s) => {
              const progress = (s.current_amount / s.target_amount) * 100;
              return (
                <Card key={s.id} className="glass-card" data-testid={`savings-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-[#0B1F3A]">{s.name}</h3>
                        <p className="text-sm text-slate-500">Week {s.weeks_completed} of {s.duration_weeks}</p>
                      </div>
                      {getStatusBadge(s.status)}
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-medium text-[#0B1F3A]">{progress.toFixed(0)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-500">Saved</p>
                        <p className="font-semibold text-[#2272C5]">{formatCurrency(s.current_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Target</p>
                        <p className="font-semibold text-[#0B1F3A]">{formatCurrency(s.target_amount)}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedSavings(s);
                          setDepositOpen(true);
                        }}
                        className="flex-1 bg-[#2272C5] hover:bg-[#1a5da8]"
                        data-testid={`deposit-${s.id}`}
                      >
                        <Coins size={16} className="mr-1" />
                        Save {formatCurrency(s.weekly_amount)}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedSavings(s);
                          setWithdrawOpen(true);
                        }}
                        data-testid={`withdraw-${s.id}`}
                      >
                        <Wallet size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card mb-6">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <Target size={24} className="text-slate-400" />
              </div>
              <h3 className="font-semibold text-[#0B1F3A] mb-1">No Active Goals</h3>
              <p className="text-sm text-slate-500 mb-4">
                Start saving towards your personal goals
              </p>
              <Button 
                onClick={() => setCreateOpen(true)}
                className="bg-[#2272C5] hover:bg-[#1a5da8]"
              >
                Create Your First Goal
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Completed/Withdrawn Savings */}
        {completedSavings.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-[#0B1F3A]">Past Goals</h2>
            {completedSavings.map((s) => (
              <Card key={s.id} className="glass-card opacity-75" data-testid={`past-savings-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#0B1F3A]">{s.name}</h3>
                      <p className="text-sm text-slate-500">
                        {formatCurrency(s.current_amount)} saved
                      </p>
                    </div>
                    {getStatusBadge(s.status)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-6 p-4 rounded-2xl bg-[#0B1F3A]/5 border border-[#0B1F3A]/10">
          <div className="flex items-start gap-3">
            <TrendingUp size={20} className="text-[#2272C5] mt-0.5" />
            <div>
              <p className="font-medium text-[#0B1F3A]">How Individual Savings Work</p>
              <p className="text-sm text-slate-500 mt-1">
                Set a savings goal, save weekly, and watch your progress. 
                Your savings are locked until you choose to withdraw.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit Dialog */}
      <AlertDialog open={depositOpen} onOpenChange={setDepositOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0B1F3A]">Make Weekly Deposit</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3 mt-2">
                <p>Deposit to "{selectedSavings?.name}":</p>
                <div className="p-4 rounded-xl bg-[#0B1F3A]/5 text-center">
                  <p className="text-2xl font-bold text-[#0B1F3A]">
                    {formatCurrency(selectedSavings?.weekly_amount || 0)}
                  </p>
                  <p className="text-sm text-slate-500">Weekly savings amount</p>
                </div>
                <p className="text-sm">
                  This amount will be moved from your available balance to your savings.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeposit}
              disabled={depositing}
              className="bg-[#2272C5] hover:bg-[#1a5da8]"
            >
              {depositing ? 'Processing...' : 'Confirm Deposit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Withdraw Dialog */}
      <AlertDialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0B1F3A]">Withdraw Savings</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3 mt-2">
                <p>Withdraw all funds from "{selectedSavings?.name}":</p>
                <div className="p-4 rounded-xl bg-amber-50 text-center">
                  <p className="text-2xl font-bold text-amber-700">
                    {formatCurrency(selectedSavings?.current_amount || 0)}
                  </p>
                  <p className="text-sm text-amber-600">Total saved</p>
                </div>
                <p className="text-sm text-slate-600">
                  This will close your savings goal and move the funds to your available balance.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleWithdraw}
              disabled={withdrawing}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {withdrawing ? 'Processing...' : 'Confirm Withdrawal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default IndividualSavings;
